/**
 * Order Confirmation Handler
 * Handles the thank you page logic and displays appropriate states
 */

export class OrderConfirmationHandler {
    constructor(system) {
        this.system = system;
        this.initialized = false;
    }

    init() {
        if (this.initialized) return;
        
        console.log('Initializing order confirmation...');
        this.handleOrderConfirmation();
        this.initialized = true;
    }

    handleOrderConfirmation() {
        // Get merchant ID from template or system config
        const merchantId = this.getMerchantId();
        
        // Get order ID and status from URL parameters
        let orderId = new URLSearchParams(window.location.search).get('orderId');
        let status = new URLSearchParams(window.location.search).get('status');

        // If orderId is not in current URL, try to get it from referrer (for CMS redirects)
        if (!orderId && document.referrer) {
            try {
                const referrerUrl = new URL(document.referrer);
                orderId = new URLSearchParams(referrerUrl.search).get('orderId');
                status = new URLSearchParams(referrerUrl.search).get('status');
                if (orderId) {
                    console.log('Found orderId in referrer URL:', orderId);
                }
            } catch (e) {
                console.log('Could not parse referrer URL:', e);
            }
        }

        // Handle different scenarios
        if (status === 'cancel') {
            this.showCancelledState();
        } else if (!orderId) {
            this.showLoadingThenGenericSuccess();
        } else {
            this.fetchOrderConfirmation(merchantId, orderId);
        }
    }

    getMerchantId() {
        // Try to get from template tokens first
        const merchantElements = document.querySelectorAll('[data-merchant-id]');
        if (merchantElements.length > 0) {
            return merchantElements[0].dataset.merchantId;
        }
        
        // Try to get from system config
        if (this.system && this.system.config && this.system.config.checkout) {
            return this.system.config.checkout.merchantId;
        }
        
        // Try to get from global config
        if (window.ecommerce && window.ecommerce.config && window.ecommerce.config.checkout) {
            return window.ecommerce.config.checkout.merchantId;
        }
        
        // Fallback - this should be replaced with actual merchant ID
        return '00000000-0000-0000-0000-000000000001';
    }

    /**
     * Show cancelled payment state
     */
    showCancelledState() {
        console.log('Payment was cancelled by user');
        this.hideAllStates();
        const cancelledState = document.getElementById('cancelled-state');
        if (cancelledState) {
            cancelledState.style.display = 'block';
        }
    }

    /**
     * Show loading state, then generic success after 3 seconds
     */
    showLoadingThenGenericSuccess() {
        console.log('No orderId found, showing loading then generic success');
        this.showLoadingState();
        
        // After 3 seconds, show generic success message
        setTimeout(() => {
            this.showGenericSuccessState();
        }, 3000);
    }

    /**
     * Show loading state
     */
    showLoadingState() {
        this.hideAllStates();
        const loadingState = document.getElementById('loading-state');
        if (loadingState) {
            loadingState.style.display = 'block';
        }
    }

    /**
     * Show generic success state (no order ID)
     */
    showGenericSuccessState() {
        this.hideAllStates();
        const successState = document.getElementById('success-generic-state');
        if (successState) {
            successState.style.display = 'block';
        }
    }

    /**
     * Show success state with order ID
     */
    showOrderSuccessState(orderId) {
        this.hideAllStates();
        const successState = document.getElementById('success-order-state');
        const orderIdDisplay = document.getElementById('order-id-display');
        
        if (successState) {
            successState.style.display = 'block';
        }
        
        if (orderIdDisplay) {
            orderIdDisplay.textContent = orderId;
        }
    }

    /**
     * Show 404 error state
     */
    showError404State() {
        this.hideAllStates();
        const errorState = document.getElementById('error-404-state');
        if (errorState) {
            errorState.style.display = 'block';
        }
    }

    /**
     * Hide all state divs
     */
    hideAllStates() {
        const states = document.querySelectorAll('.thank-you-state');
        states.forEach(state => {
            state.style.display = 'none';
        });
    }

    /**
     * Fetch order confirmation from API
     */
    async fetchOrderConfirmation(merchantId, orderId) {
        // Show loading state while fetching
        this.showLoadingState();

        try {
            const response = await fetch(`/cart/api/v1/order/confirm?merchantId=${merchantId}&orderId=${orderId}`, {
                method: 'GET',
                headers: {
                    'Accept-Language': 'sv-SE,sv;',
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                // Check if response has content before parsing JSON
                const text = await response.text();
                
                if (!text || text.trim() === '' || text === 'null') {
                    // Empty response - show success with order ID (Bambora/Stripe/None orders)
                    console.log('Empty response from API, showing success state with order ID');
                    this.showOrderSuccessState(orderId);
                    return;
                }
                
                const data = JSON.parse(text);
                console.log('Order confirmation loaded successfully');
                
                // Check if we got an htmlSnippet (Klarna confirmation)
                if (data.htmlSnippet) {
                    // Hide all states
                    this.hideAllStates();
                    
                    // Inject Klarna snippet into container
                    const klarnaContainer = document.getElementById('klarna');
                    if (klarnaContainer) {
                        klarnaContainer.innerHTML = data.htmlSnippet;
                        klarnaContainer.style.display = 'block';
                        this.executeScripts(klarnaContainer);
                    }
                } else {
                    // No Klarna snippet, show generic success with order ID
                    this.showOrderSuccessState(orderId);
                }
            } else if (response.status === 404) {
                // Order not found
                throw new Error('404');
            } else {
                // Other error - show generic success with order ID
                // (This handles cases where callback already processed the order)
                this.showOrderSuccessState(orderId);
            }
        } catch (error) {
            console.log('Error loading order confirmation:', error.message);
            this.handleConfirmationError(orderId, error);
        }
    }

    /**
     * Handle errors when fetching confirmation
     */
    handleConfirmationError(orderId, error) {
        if (error.message.includes('404')) {
            // Show 404 error state
            this.showError404State();
        } else {
            // Generic error - show success with order ID
            // (likely Bambora/Stripe orders where callback already processed)
            this.showOrderSuccessState(orderId);
        }
    }

    /**
     * Execute scripts in the response (for Klarna compatibility)
     */
    executeScripts(container) {
        const scriptTags = container.getElementsByTagName('script');
        for (let i = 0; i < scriptTags.length; i++) {
            const parentNode = scriptTags[i].parentNode;
            const newScriptTag = document.createElement('script');
            newScriptTag.type = 'text/javascript';
            newScriptTag.text = scriptTags[i].text;

            parentNode.removeChild(scriptTags[i]);
            parentNode.appendChild(newScriptTag);
        }
    }
}

// Auto-initialize if we're on a thank you page
document.addEventListener('DOMContentLoaded', function() {
    const klarnaContainer = document.getElementById('klarna');
    
    // CRITICAL: Only initialize on thank you pages, NOT on checkout pages
    // Check multiple indicators to ensure we're on the right page
    const isCheckoutPage = window.templateType === 'Checkout' ||
                          window.location.pathname.toLowerCase().includes('/betala/') ||
                          window.location.pathname.toLowerCase().includes('/checkout');
    
    if (klarnaContainer && !isCheckoutPage) {
        console.log('OrderConfirmationHandler: Initializing on thank you page');
        // Wait for ecommerce system to be ready (if available)
        const initHandler = () => {
            const system = window.ecommerce || null;
            const handler = new OrderConfirmationHandler(system);
            handler.init();
        };
        
        // Initialize immediately since this doesn't depend on the full ecommerce system
        initHandler();
    } else if (isCheckoutPage) {
        console.log('OrderConfirmationHandler: Skipping initialization on checkout page');
    }
});
